import { Link } from "@tanstack/react-router";
import { ScreenShell } from "./ScreenShell";
import { useAgentLearningInsights, useRunAgentLearning, useTemplatePerformance, useUpdateAgentLearningStatus } from "../../queries/useAgentLearning";
import type { AgentLearningInsight, AgentLearningStatus, TemplatePerformanceItem } from "../../queries/useAgentLearning";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const statusLabels: Record<AgentLearningStatus, string> = {
  new: "Needs Marc",
  watching: "Watching",
  accepted: "Noted",
  rejected: "Rejected",
  experiment: "Golden PR",
};

const categoryLabels: Record<AgentLearningInsight["category"], string> = {
  timing: "Timing",
  copy: "Copy",
  conversion: "Conversion",
  dashboard: "Dashboard",
  compliance: "Compliance",
  voice: "Voice",
  data_quality: "Data quality",
};

export function InsightsContent() {
  const insights = useAgentLearningInsights();
  const runLearning = useRunAgentLearning();
  const updateStatus = useUpdateAgentLearningStatus();
  const templatePerformance = useTemplatePerformance();
  const items = insights.data?.items || [];
  const openItems = items.filter((item) => ["new", "watching", "experiment"].includes(item.status));
  const reviewedItems = items.filter((item) => !["new", "watching", "experiment"].includes(item.status));

  return (
    <ScreenShell
      badge="Review"
      title="Murray Review"
      subtitle="One place for the cases where Murray needs owner judgment. Observation only: no template changes, customer sends, or Vonigo writes."
      unframed
    >
      <div className="space-y-5">
        <div className="flex flex-col gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold">Safe review mode</p>
            <p className="mt-1 text-emerald-800">
              Marc decisions become learning evidence. This page cannot send messages, edit templates, or write Vonigo.
            </p>
          </div>
          <Button
            onClick={() => runLearning.mutate()}
            disabled={runLearning.isPending}
            className="shrink-0"
          >
            {runLearning.isPending ? "Analyzing" : "Refresh observations"}
          </Button>
        </div>

        {insights.isLoading ? (
          <EmptyState title="Loading review queue" body="Murray is reading the current learning log." />
        ) : insights.isError ? (
          <EmptyState title="Review queue unavailable" body="The backend could not load the current cases." />
        ) : items.length === 0 ? (
          <EmptyState
            title="No review cases yet"
            body="If data is light, Murray will stay quiet instead of guessing."
          />
        ) : (
          <>
            <section className="space-y-3">
              <div className="flex items-end justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-slate-950">Needs Marc</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {openItems.length} open case{openItems.length === 1 ? "" : "s"} waiting for owner judgment.
                  </p>
                </div>
              </div>
              {openItems.length ? (
                <div className="grid gap-3">
                  {openItems.map((item) => (
                    <InsightCard
                      key={item.id}
                      insight={item}
                      onStatus={(status) => updateStatus.mutate({ id: item.id, status })}
                      isUpdating={updateStatus.isPending}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState title="Nothing waiting on Marc" body="New checker stops and label-shadow patterns will land here." />
              )}
            </section>

            {reviewedItems.length ? (
              <section className="space-y-3">
                <h2 className="text-base font-semibold text-slate-950">Already reviewed</h2>
                <div className="grid gap-3">
                  {reviewedItems.map((item) => (
                    <InsightCard
                      key={item.id}
                      insight={item}
                      onStatus={(status) => updateStatus.mutate({ id: item.id, status })}
                      isUpdating={updateStatus.isPending}
                      compact
                    />
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}

        <TemplatePerformancePanel
          isLoading={templatePerformance.isLoading}
          isError={templatePerformance.isError}
          items={templatePerformance.data?.items || []}
          totalMessages={templatePerformance.data?.total_messages || 0}
          caveats={templatePerformance.data?.caveats || []}
        />
      </div>
    </ScreenShell>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center">
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <p className="mx-auto mt-1 max-w-xl text-sm text-slate-600">{body}</p>
    </div>
  );
}

function TemplatePerformancePanel({
  isLoading,
  isError,
  items,
  totalMessages,
  caveats,
}: {
  isLoading: boolean;
  isError: boolean;
  items: TemplatePerformanceItem[];
  totalMessages: number;
  caveats: string[];
}) {
  return (
    <Card className="shadow-none">
      <CardHeader>
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle className="text-base">Template performance scoreboard</CardTitle>
            <p className="mt-1 text-sm text-slate-500">
              Read-only attribution across sent emails and SMS. Good for deciding what to test, not for declaring automatic winners.
            </p>
          </div>
          <span className="text-xs text-slate-500">{totalMessages} tracked sends</span>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-slate-500">Loading template performance.</p>
        ) : isError ? (
          <p className="text-sm text-slate-500">Template performance is unavailable.</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">No measurable template sends yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="px-2 py-2 font-semibold">Template</th>
                  <th className="px-2 py-2 font-semibold">Sends</th>
                  <th className="px-2 py-2 font-semibold">Replies</th>
                  <th className="px-2 py-2 font-semibold">Scheduled</th>
                  <th className="px-2 py-2 font-semibold">Opt-out</th>
                  <th className="px-2 py-2 font-semibold">Read</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item) => (
                  <TemplatePerformanceRow key={`${item.channel}:${item.template_family}`} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {caveats.length ? (
          <div className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {caveats.slice(0, 3).map((caveat) => (
              <p key={caveat}>{caveat}</p>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function TemplatePerformanceRow({ item }: { item: TemplatePerformanceItem }) {
  return (
    <tr>
      <td className="max-w-[280px] px-2 py-3">
        <div className="font-medium text-slate-900">{item.template_family}</div>
        <div className="text-xs text-slate-500">{item.channel} · confidence {item.confidence}</div>
      </td>
      <td className="px-2 py-3 text-slate-700">{item.sends}</td>
      <td className="px-2 py-3 text-slate-700">
        {item.replies} <span className="text-xs text-slate-400">({formatPct(item.reply_rate)})</span>
      </td>
      <td className="px-2 py-3 text-slate-700">
        {item.scheduled_after_send} <span className="text-xs text-slate-400">({formatPct(item.conversion_rate)})</span>
      </td>
      <td className="px-2 py-3 text-slate-700">
        {item.opt_outs} <span className="text-xs text-slate-400">({formatPct(item.opt_out_rate)})</span>
      </td>
      <td className="px-2 py-3">
        <span className={verdictClass(item.verdict)}>{verdictLabel(item.verdict)}</span>
      </td>
    </tr>
  );
}

function formatPct(value: number) {
  return `${(value * 100).toFixed(1)}%`;
}

function verdictLabel(value: TemplatePerformanceItem["verdict"]) {
  if (value === "candidate") return "Candidate";
  if (value === "risk_review") return "Risk review";
  if (value === "watch") return "Watch";
  return "Needs data";
}

function verdictClass(value: TemplatePerformanceItem["verdict"]) {
  const base = "inline-flex rounded-full px-2 py-1 text-xs font-medium";
  if (value === "candidate") return `${base} bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200`;
  if (value === "risk_review") return `${base} bg-red-50 text-red-700 ring-1 ring-red-200`;
  if (value === "watch") return `${base} bg-blue-50 text-blue-700 ring-1 ring-blue-200`;
  return `${base} bg-slate-50 text-slate-600 ring-1 ring-slate-200`;
}

function InsightCard({
  insight,
  onStatus,
  isUpdating,
  compact = false,
}: {
  insight: AgentLearningInsight;
  onStatus: (status: AgentLearningStatus) => void;
  isUpdating: boolean;
  compact?: boolean;
}) {
  const evidence = insight.evidence || {};
  const samples = evidenceSamples(evidence);
  return (
    <Card className="shadow-none">
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
              {categoryLabels[insight.category]}
            </span>
            <span className="rounded-full bg-white px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
              {statusLabels[insight.status]}
            </span>
            <span className="text-xs text-slate-500">Confidence {insight.confidence}</span>
          </div>
          <CardTitle className="mt-3 text-base">{insight.title}</CardTitle>
        </div>
        <select
          value={insight.status}
          disabled={isUpdating}
          onChange={(event) => onStatus(event.target.value as AgentLearningStatus)}
          className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700"
          aria-label="Observation status"
          title="Changing this status never edits templates or sends messages."
        >
          {Object.entries(statusLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-700">{insight.summary}</p>
        <div className="rounded-md bg-slate-50 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Question for Marc</p>
          <p className="mt-1 text-sm text-slate-800">{insight.recommendation}</p>
        </div>
        {!compact && samples.length ? <SampleTable samples={samples} /> : null}
        <div className="flex flex-wrap gap-2 text-xs text-slate-500">
          {typeof evidence.total_sends === "number" ? <span>{evidence.total_sends} sends</span> : null}
          {typeof evidence.personalized_sends === "number" ? <span>{evidence.personalized_sends} personalized</span> : null}
          {typeof evidence.days_back === "number" ? <span>{evidence.days_back} day window</span> : null}
          {typeof evidence.total_proposals === "number" ? <span>{evidence.total_proposals} proposals</span> : null}
          {typeof evidence.review_needed === "number" ? <span>{evidence.review_needed} checker stops</span> : null}
          <span>Risk {insight.risk}</span>
          <span>Updated {new Date(insight.updated_at).toLocaleString()}</span>
        </div>
      </CardContent>
    </Card>
  );
}

type EvidenceSample = {
  quote_id?: string;
  channel?: string;
  label?: string;
  action?: string;
  reason?: string;
  confidence?: number;
  status?: string;
  created_at?: string;
};

function evidenceSamples(evidence: Record<string, unknown>): EvidenceSample[] {
  if (!Array.isArray(evidence.samples)) return [];
  return evidence.samples.slice(0, 5).map((raw) => {
    const row = isRecord(raw) ? raw : {};
    return {
      quote_id: text(row.quote_id),
      channel: text(row.channel),
      label: text(row.proposed_label),
      action: text(row.proposed_action),
      reason: text(row.top_reason),
      confidence: number(row.confidence),
      status: text(row.current_status),
      created_at: text(row.created_at),
    };
  });
}

function SampleTable({ samples }: { samples: EvidenceSample[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-100">
      <table className="min-w-full text-left text-xs">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="px-3 py-2 font-semibold">Quote</th>
            <th className="px-3 py-2 font-semibold">Signal</th>
            <th className="px-3 py-2 font-semibold">Reason</th>
            <th className="px-3 py-2 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {samples.map((sample, index) => (
            <tr key={`${sample.quote_id || "sample"}-${index}`}>
              <td className="px-3 py-2 font-medium text-slate-900">
                {sample.quote_id ? (
                  <Link className="text-blue-700 hover:text-blue-900" to="/quotes/$quoteId" params={{ quoteId: sample.quote_id }}>
                    #{sample.quote_id}
                  </Link>
                ) : "-"}
              </td>
              <td className="px-3 py-2 text-slate-700">
                {sample.label || sample.action || "-"}
                {sample.channel ? <span className="ml-1 text-slate-400">({sample.channel})</span> : null}
              </td>
              <td className="px-3 py-2 text-slate-600">
                {sample.reason || (sample.confidence ? `confidence ${sample.confidence.toFixed(2)}` : "-")}
              </td>
              <td className="px-3 py-2 text-slate-500">{sample.status || shortDate(sample.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function text(value: unknown): string | undefined {
  const normalized = String(value || "").trim();
  return normalized || undefined;
}

function number(value: unknown): number | undefined {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : undefined;
}

function shortDate(value: string | undefined): string {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
}
